import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './agent.css';
import {
  GET_AI_AGENTS,
  POST_AI_AGENT,
  POST_WEBSITE_KNOWLEDGE,
  POST_DOCUMENT_KNOWLEDGE,
} from '../../api/aiKnowledge.service';
import { API_GET_FILE_URL, API_UPLOAD_FILE } from '../../api/media.service';
import { useLayout } from '../../context/LayoutContext';

const AgentCreate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toastMessage } = useLayout();

  const providerModels = {
    openai: ['gpt-4o-mini'],
  };

  const [agentName, setAgentName] = useState('');
  const [provider, setProvider] = useState('openai');
  const [model, setModel] = useState(providerModels.openai[0]);
  const [saving, setSaving] = useState(false);
  const [createStep, setCreateStep] = useState(1);
  const [sourceName, setSourceName] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [createdAgentId, setCreatedAgentId] = useState('');
  const [createdAgentName, setCreatedAgentName] = useState('');
  const [fileSourceName, setFileSourceName] = useState('');
  const [documentUrl, setDocumentUrl] = useState('');
  const [documentName, setDocumentName] = useState('');
  const [documentUploading, setDocumentUploading] = useState(false);
  const [documentError, setDocumentError] = useState('');
  const [formError, setFormError] = useState('');
  const [knowledgeTypeLoading, setKnowledgeTypeLoading] = useState(false);
  const [existingKnowledge, setExistingKnowledge] = useState({
    hasWebsite: false,
    hasDocument: false,
  });
  const fileInputRef = useRef(null);

  const getActionMessage = (payload, fallback) => {
    const normalizeMessage = (value) =>
      Array.isArray(value) ? value.filter(Boolean).join(', ') : value;

    if (typeof payload === 'string') return payload;
    return (
      normalizeMessage(payload?.response?.data?.message) ||
      normalizeMessage(payload?.data?.message) ||
      normalizeMessage(payload?.message) ||
      normalizeMessage(payload?.error) ||
      fallback
    );
  };

  const showActionToast = (severity, payload, fallback) => {
    toastMessage({
      severity,
      detail: getActionMessage(payload, fallback),
    });
  };

  const canSaveKnowledge = Boolean(
    (!existingKnowledge.hasWebsite && websiteUrl.trim()) ||
      (!existingKnowledge.hasDocument && documentUrl),
  );

  const resetDocumentState = () => {
    setFileSourceName('');
    setDocumentUrl('');
    setDocumentName('');
    setDocumentError('');
    setDocumentUploading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const resetKnowledgeInputs = () => {
    setSourceName('');
    setWebsiteUrl('');
    resetDocumentState();
  };

  const fetchAgents = async () => {
    try {
      const resp = await GET_AI_AGENTS();
      const rows = Array.isArray(resp?.data)
        ? resp.data
        : Array.isArray(resp?.data?.data)
          ? resp.data.data
          : resp?.data?.rows || [];
      return rows.map((item) => ({
        id: item?.agent?.id || item?.agentId || item?.id || '',
        name: item?.agent?.agentName || item?.agentName || '',
        updatedAt:
          item?.agent?.updatedAt ||
          item?.updatedAt ||
          item?.agent?.createdAt ||
          item?.createdAt ||
          null,
      }));
    } catch (err) {
      return [];
    }
  };

  const getExistingKnowledgeState = async (agentId) => {
    if (!agentId) {
      return { hasWebsite: false, hasDocument: false };
    }

    try {
      const resp = await GET_AI_AGENTS();
      const rows = Array.isArray(resp?.data)
        ? resp.data
        : Array.isArray(resp?.data?.data)
          ? resp.data.data
          : resp?.data?.rows || [];
      const knowledgeList = [];

      rows.forEach((item) => {
        const currentAgentId = String(item?.agent?.id || item?.agentId || item?.id || '');
        if (currentAgentId !== String(agentId)) return;

        if (item?.agent || item?.knowledge) {
          if (Array.isArray(item?.knowledge)) {
            knowledgeList.push(...item.knowledge);
          }
          return;
        }

        if (item?.knowledgeId || item?.knowldgeType || item?.knowledgeType) {
          knowledgeList.push(item);
        }
      });

      const knowledgeTypes = knowledgeList
        .map((item) => String(item?.knowldgeType || item?.knowledgeType || '').toLowerCase())
        .filter(Boolean);

      return {
        hasWebsite: knowledgeTypes.some((type) => type.includes('website')),
        hasDocument: knowledgeTypes.some((type) => type.includes('document')),
      };
    } catch (err) {
      return { hasWebsite: false, hasDocument: false };
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const step = params.get('step');
    const agentId = params.get('agentId');
    const agentNameParam = params.get('agentName');

    if (step === '2' && agentId) {
      setCreatedAgentId(String(agentId));
      setCreatedAgentName(agentNameParam || '');
      setCreateStep(2);
    }
  }, [location.search]);

  useEffect(() => {
    const options = providerModels[provider] || [];
    if (options.length === 0) return;
    if (!options.includes(model)) {
      setModel(options[0]);
    }
  }, [provider]);

  useEffect(() => {
    if (createStep !== 2 || !createdAgentId) {
      setExistingKnowledge({ hasWebsite: false, hasDocument: false });
      setKnowledgeTypeLoading(false);
      return;
    }

    let ignore = false;

    const loadExistingKnowledge = async () => {
      setKnowledgeTypeLoading(true);
      const knowledgeState = await getExistingKnowledgeState(createdAgentId);
      if (ignore) return;

      setExistingKnowledge(knowledgeState);

      if (knowledgeState.hasWebsite) {
        setSourceName('');
        setWebsiteUrl('');
      }

      if (knowledgeState.hasDocument) {
        resetDocumentState();
      }

      setKnowledgeTypeLoading(false);
    };

    loadExistingKnowledge();

    return () => {
      ignore = true;
    };
  }, [createStep, createdAgentId]);

  const handleDocumentSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const fileName = file.name || '';
    const extension = fileName.split('.').pop()?.toLowerCase();
    const allowedExtensions = ['pdf', 'doc', 'docx', 'txt', 'csv', 'xls', 'xlsx'];

    if (!extension || !allowedExtensions.includes(extension)) {
      const message = 'Supported files: PDF, DOC, DOCX, TXT, CSV, XLS, XLSX.';
      setDocumentError(message);
      setDocumentName('');
      setDocumentUrl('');
      showActionToast('warn', null, message);
      event.target.value = '';
      return;
    }

    setDocumentError('');
    setDocumentName(fileName);
    setDocumentUploading(true);
    setDocumentUrl('');

    try {
      const resp = await API_GET_FILE_URL();
      const uploadSrc = resp?.data?.src;
      const uploadUrl = resp?.data?.url;
      if (!uploadSrc || !uploadUrl) {
        throw new Error('Upload URL not available.');
      }
      await API_UPLOAD_FILE(
        uploadSrc,
        file,
        file.type || 'application/octet-stream',
      );
      setDocumentUrl(uploadUrl);
      showActionToast('success', null, 'Document uploaded successfully.');
    } catch (err) {
      const message = getActionMessage(err, err?.message || 'Upload failed. Please try again.');
      setDocumentError(message);
      showActionToast('error', err, message);
    } finally {
      setDocumentUploading(false);
      event.target.value = '';
    }
  };

  const handleNextStep = async () => {
    const trimmedName = agentName.trim();
    if (!trimmedName) return;
    setSaving(true);
    setFormError('');
    resetKnowledgeInputs();

    const payload = {
      agentName: trimmedName,
      providerName: provider,
      llmModel: model,
    };

    try {
      const createResp = await POST_AI_AGENT(payload);
      let agentId =
        createResp?.data?.id ||
        createResp?.data?.data?.id ||
        createResp?.id ||
        createResp?.data?.agent?.id;

      if (!agentId) {
        const updatedAgents = await fetchAgents();
        const matched = updatedAgents
          .filter((agent) => agent.name?.toLowerCase() === trimmedName.toLowerCase())
          .sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0));
        agentId = matched[0]?.id || updatedAgents[0]?.id;
      }

      if (!agentId) {
        throw new Error('Agent created but details could not be refreshed.');
      }

      setCreatedAgentId(String(agentId));
      setCreatedAgentName(trimmedName);
      setCreateStep(2);
      showActionToast('success', createResp, 'AI agent created successfully.');
    } catch (err) {
      const message = getActionMessage(err, 'Failed to create agent.');
      setFormError(message);
      showActionToast('error', err, message);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (createStep === 1) {
      handleNextStep();
      return;
    }

    const trimmedSource = sourceName.trim();
    const trimmedUrl = websiteUrl.trim();

    if (!createdAgentId) return;

    try {
      const normalizedUrl = !trimmedUrl
        ? ''
        : trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')
          ? trimmedUrl
          : `https://${trimmedUrl}`;
      const trimmedFileSource = fileSourceName.trim();
      const documentSource = trimmedFileSource || trimmedSource;

      const requests = [];
      if (normalizedUrl && !existingKnowledge.hasWebsite) {
        requests.push(
          POST_WEBSITE_KNOWLEDGE({
            KnowledgeUrl: normalizedUrl,
            agentId: createdAgentId,
            sourceName: trimmedSource,
          }),
        );
      }

      if (documentUrl && !existingKnowledge.hasDocument) {
        requests.push(
          POST_DOCUMENT_KNOWLEDGE({
            documentUrl,
            agentId: createdAgentId,
            sourceName: documentSource,
          }),
        );
      }

      if (requests.length === 0) return;

      setSaving(true);
      setFormError('');
      const responses = await Promise.all(requests);

      setAgentName('');
      resetKnowledgeInputs();
      setCreatedAgentId('');
      setCreatedAgentName('');
      setExistingKnowledge({ hasWebsite: false, hasDocument: false });
      setKnowledgeTypeLoading(false);
      setCreateStep(1);
      const successMessage =
        responses.length > 1
          ? 'Knowledge sources saved successfully.'
          : getActionMessage(
              responses[0],
              normalizedUrl
                ? 'Website knowledge added successfully.'
                : 'Document knowledge added successfully.',
            );
      showActionToast('success', null, successMessage);
    } catch (err) {
      const message = getActionMessage(err, 'Failed to save knowledge.');
      setFormError(message);
      showActionToast('error', err, message);
    } finally {
      setSaving(false);
    }
  };

  const handleBackStep = () => {
    setCreateStep(1);
    setCreatedAgentId('');
    setCreatedAgentName('');
    setExistingKnowledge({ hasWebsite: false, hasDocument: false });
    setKnowledgeTypeLoading(false);
    resetKnowledgeInputs();
  };

  const handleCancel = () => {
    setAgentName('');
    setProvider('openai');
    setModel(providerModels.openai[0]);
    setCreateStep(1);
    resetKnowledgeInputs();
    setCreatedAgentId('');
    setCreatedAgentName('');
    setExistingKnowledge({ hasWebsite: false, hasDocument: false });
    setKnowledgeTypeLoading(false);
    setFormError('');
    navigate('/ai-agent');
  };

  return (
    <div className="agent-create">
      <div className="agent-create__header">
        <div>
          <div className="agent-create__title">Add AI Agent</div>
          <div className="agent-create__subtitle">
            Create a new agent and add knowledge sources.
          </div>
        </div>
        <button
          type="button"
          className="agent-ui__btn agent-ui__btn--ghost"
          onClick={() => navigate('/ai-agent')}
        >
          Back
        </button>
      </div>

      <div className="agent-create__container">
        <form className="agent-ui__card agent-create__card" onSubmit={handleSubmit}>
          <div className="agent-ui__card-header agent-ui__card-header--stack">
            <div className="agent-ui__stepper">
              <span className={`agent-ui__step-pill ${createStep === 1 ? 'is-active' : ''}`}>
                Step 1
              </span>
              <span className="agent-ui__step-sep">-</span>
              <span className={`agent-ui__step-pill ${createStep === 2 ? 'is-active' : ''}`}>
                Step 2
              </span>
            </div>
            <div className="agent-ui__card-title">
              {createStep === 1 ? 'Create Agent' : 'Add Knowledge Source'}
            </div>
            <div className="agent-ui__card-subtitle">
              {createStep === 1
                ? 'Configure provider, model, and deployment settings.'
                : 'Add a website or document source to train this agent.'}
            </div>
            <div className="agent-ui__divider" />
          </div>

          <div className="agent-ui__body">
            {createStep === 1 ? (
              <>
                <label className="agent-ui__label">Agent Name</label>
                <input
                  className="agent-ui__input"
                  value={agentName}
                  onChange={(event) => setAgentName(event.target.value)}
                  placeholder="e.g. Support Assistant"
                />

                <label className="agent-ui__label">Provider</label>
                <select
                  className="agent-ui__input"
                  value={provider}
                  onChange={(event) => setProvider(event.target.value)}
                >
                  <option value="openai">OpenAI</option>
                  {/* <option value="anthropic">Anthropic</option> */}
                  {/* <option value="google">Gemini</option> */}
                </select>
                <div className="agent-ui__hint">Select the AI service provider for this agent.</div>

                <label className="agent-ui__label">Model</label>
                <select
                  className="agent-ui__input"
                  value={model}
                  onChange={(event) => setModel(event.target.value)}

                >
                  {(providerModels[provider] || []).map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
                <div className="agent-ui__hint">Select the LLM model that the agent will use.</div>
              </>
            ) : (
              <>
                <label className="agent-ui__label">Agent Name</label>
                <input
                  className="agent-ui__input"
                  value={createdAgentName || agentName || ''}
                  readOnly
                />

                {knowledgeTypeLoading ? (
                  <div className="agent-ui__hint">Checking existing knowledge sources...</div>
                ) : (
                  <>
                    {existingKnowledge.hasWebsite ? (
                      <div className="agent-ui__hint">
                        Website knowledge is already added for this agent.
                      </div>
                    ) : (
                      <>
                        <label className="agent-ui__label">Source Name</label>
                        <input
                          className="agent-ui__input"
                          value={sourceName}
                          onChange={(event) => setSourceName(event.target.value)}
                          placeholder="Source name"
                        />

                        <label className="agent-ui__label">Website URL</label>
                        <input
                          className="agent-ui__input"
                          value={websiteUrl}
                          onChange={(event) => setWebsiteUrl(event.target.value)}
                          placeholder="https://example.com"
                        />
                        <div className="agent-ui__hint">
                          Enter a public URL for knowledge ingestion.
                        </div>
                      </>
                    )}

                    {existingKnowledge.hasDocument ? (
                      <div className="agent-ui__hint">
                        Document knowledge is already added for this agent.
                      </div>
                    ) : (
                      <>
                        <label className="agent-ui__label">Document Source Name</label>
                        <input
                          className="agent-ui__input"
                          value={fileSourceName}
                          onChange={(event) => setFileSourceName(event.target.value)}
                          placeholder="Document source name"
                        />

                        <label className="agent-ui__label">Upload Document</label>
                        <div className="agent-ui__file-box">
                          <input
                            ref={fileInputRef}
                            type="file"
                            className="agent-ui__file-input"
                            accept=".pdf,.doc,.docx,.txt,.csv,.xls,.xlsx"
                            onChange={handleDocumentSelect}
                            disabled={documentUploading}
                          />
                          <div className="agent-ui__file-row">
                            <button
                              type="button"
                              className="agent-ui__btn agent-ui__btn--ghost agent-ui__file-button"
                              onClick={() => fileInputRef.current?.click()}
                              disabled={documentUploading}
                            >
                              {documentUploading ? 'Uploading...' : 'Choose File'}
                            </button>
                            <div className="agent-ui__file-meta">
                              <div className="agent-ui__file-name">
                                {documentName || 'No file chosen'}
                              </div>
                              <div className="agent-ui__hint">
                                {documentUrl
                                  ? 'Upload complete.'
                                  : 'PDF, DOC, DOCX, TXT, CSV, XLS, XLSX'}
                              </div>
                            </div>
                          </div>
                          {documentError ? (
                            <div className="agent-ui__hint agent-ui__hint--error">
                              {documentError}
                            </div>
                          ) : null}
                        </div>
                      </>
                    )}
                  </>
                )}
              </>
            )}

            {formError ? (
              <div className="agent-ui__hint agent-ui__hint--error">{formError}</div>
            ) : null}
            <div className="agent-ui__inline-actions" />
          </div>

          <div className="agent-ui__footer agent-ui__footer--actions">
            {/* <button
              type="button"
              className="agent-ui__btn agent-ui__btn--ghost"
              onClick={handleBackStep}
              disabled={createStep === 1}
            >
              Back
            </button> */}
            <div className="agent-ui__footer-right">
              <button
                type="button"
                className="agent-ui__btn agent-ui__btn--ghost"
                onClick={handleCancel}
              >
                Cancel
              </button>
              {createStep === 1 ? (
                <button
                  type="button"
                  className="agent-ui__btn agent-ui__btn--primary"
                  onClick={handleNextStep}
                  disabled={saving || !agentName.trim()}
                >
                  Next
                </button>
              ) : (
                <button
                  type="submit"
                  className="agent-ui__btn agent-ui__btn--primary"
                  disabled={saving || documentUploading || !canSaveKnowledge}
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AgentCreate;
